import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  ligarAvisosGerais,
  suportaAvisos,
  estadoPermissao,
  precisaInstalarNoIphone,
} from '@/lib/avisos';
import {
  oracaoDeHoje,
  minhaOracaoSituacao,
  marcarRezei,
  aceitarDesafio,
  oracoesAdminListar,
  souAdminPlataforma,
  corLiturgica,
  LINK_ORACAO,
  type OracaoHoje,
  type SituacaoOracao,
} from '@/lib/oracao';
import BarraProgresso from './components/BarraProgresso';
import { marcarOracaoVista } from '@/lib/oracaoVista';
import ConteudoPasso from './components/ConteudoPasso';
import type { Passo } from './types';

const ESCALAS = [1, 1.12, 1.25];
const CHAVE_FONTE = 'seliga_oracao_fonte';

function lerEscala(): number {
  try {
    const v = Number(localStorage.getItem(CHAVE_FONTE));
    return Number.isInteger(v) && v >= 0 && v < ESCALAS.length ? v : 1; // padrão 1.12
  } catch {
    return 1;
  }
}

// Página "Oração de hoje": oração guiada (Lectio Divina), um passo por tela,
// numa "capela no bolso" escura. Rota livre — abre sem login e sem o app.
export default function OracaoPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const parametroDia = params.get('dia');

  const [carregando, setCarregando] = useState(true);
  const [oracao, setOracao] = useState<OracaoHoje | null>(null);
  const [situacao, setSituacao] = useState<SituacaoOracao | null>(null);
  const [previa, setPrevia] = useState(false);
  const [indice, setIndice] = useState(0);
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [escalaIdx, setEscalaIdx] = useState(lerEscala);

  const [enviandoRezei, setEnviandoRezei] = useState(false);
  const [enviandoDesafio, setEnviandoDesafio] = useState(false);
  const [desafioAceito, setDesafioAceito] = useState(false);
  const [desafiosHoje, setDesafiosHoje] = useState(0);
  const [clarao, setClarao] = useState(false);

  const [mostrarAvisos, setMostrarAvisos] = useState(false);
  const [ativandoAvisos, setAtivandoAvisos] = useState(false);

  const toqueX = useRef<number | null>(null);
  const toqueY = useRef<number | null>(null);
  const indiceAnterior = useRef(0);

  useEffect(() => {
    setMostrarAvisos(
      suportaAvisos() && !precisaInstalarNoIphone() && estadoPermissao() !== 'granted',
    );
  }, []);

  useEffect(() => {
    // Abrir a oração já conta como "vista hoje" (o ponto "Nova" da Início some).
    marcarOracaoVista();
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      setPrevia(false);
      setDesafioAceito(false);
      setDesafiosHoje(0);
      try {
        if (parametroDia && /^\d{4}-\d{2}-\d{2}$/.test(parametroDia)) {
          const admin = await souAdminPlataforma();
          if (admin) {
            const lista = await oracoesAdminListar(parametroDia, 1);
            if (!vivo) return;
            setPrevia(true);
            setOracao(lista[0] ?? { tem: false });
            return;
          }
        }
        const [o, s] = await Promise.all([oracaoDeHoje(), minhaOracaoSituacao()]);
        if (!vivo) return;
        setOracao(o);
        setSituacao(s);
        if (s) {
          setDesafioAceito(s.aceitei_desafio);
          setDesafiosHoje(s.desafios_hoje);
        }
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [parametroDia]);

  const passos = useMemo<Passo[]>(() => {
    if (!oracao || oracao.tem !== true) return [];
    const l: Passo[] = [
      { id: 'capa', rotulo: '', titulo: '' },
      { id: 'presenca', rotulo: 'Preparação', titulo: 'Presença de Deus' },
    ];
    if (oracao.santo) l.push({ id: 'santo', rotulo: 'Santo do dia', titulo: oracao.santo });
    l.push({ id: 'biblia', rotulo: 'Leitura', titulo: 'Pegue a sua Bíblia' });
    if (oracao.resumo) l.push({ id: 'entenda', rotulo: 'Leitura', titulo: 'O que você leu' });
    if (oracao.video_url)
      l.push({ id: 'padre', rotulo: 'Palavra do padre', titulo: 'Uma palavra para hoje' });
    if (oracao.reflexao) l.push({ id: 'medite', rotulo: 'Meditação', titulo: 'Medite' });
    if (oracao.perguntas)
      l.push({ id: 'vida', rotulo: 'Meditação', titulo: 'Olhe para a sua vida' });
    if (oracao.texto) l.push({ id: 'reze', rotulo: 'Oração', titulo: 'Reze' });
    if (oracao.silencio)
      l.push({ id: 'contemple', rotulo: 'Contemplação', titulo: 'Um minuto de silêncio' });
    if (oracao.gesto) l.push({ id: 'viva', rotulo: 'Ação', titulo: 'Desafio do dia' });
    l.push({ id: 'termine', rotulo: 'Conclusão', titulo: 'Reze o Pai-Nosso' });
    l.push({ id: 'final', rotulo: '', titulo: '' });
    return l;
  }, [oracao]);

  const perguntas = useMemo(
    () =>
      (oracao?.perguntas ?? '')
        .split(/\r?\n/)
        .map((p) => p.trim())
        .filter(Boolean),
    [oracao],
  );

  useEffect(() => {
    setIndice(0);
  }, [passos]);

  const cor = corLiturgica(oracao?.cor_liturgica);
  const escala = ESCALAS[escalaIdx];
  const temOracao = oracao?.tem === true;
  const semOracao = !carregando && !temOracao;
  const passoAtual = passos[indice] ?? null;

  // Ao entrar em "vida", a primeira pergunta vem ao avançar e a última ao voltar.
  useEffect(() => {
    const passo = passos[indice];
    if (passo && passo.id === 'vida') {
      const paraFrente = indice >= indiceAnterior.current;
      setPerguntaAtual(paraFrente ? 0 : Math.max(0, perguntas.length - 1));
    }
    indiceAnterior.current = indice;
  }, [indice, passos, perguntas.length]);

  // A oração é uma capela em tela cheia: enquanto ela está aberta, escondemos
  // o cabeçalho do site. Assim o único jeito de sair é o X (o "Voltar" do pé
  // da página sempre anda um passo para trás, nunca volta para a Início).
  useEffect(() => {
    document.body.classList.add('oracao-ativa');
    return () => {
      document.body.classList.remove('oracao-ativa');
    };
  }, []);

  // theme-color acompanha a cor da "capela"; restaura ao sair.
  useEffect(() => {    const meta = document.querySelector('meta[name="theme-color"]');
    const anterior = meta?.getAttribute('content') ?? null;
    if (meta) meta.setAttribute('content', cor.nave);
    return () => {
      if (meta && anterior !== null) meta.setAttribute('content', anterior);
    };
  }, [cor.nave]);

  // Mantém a tela acesa enquanto a pessoa reza (quando o aparelho deixa).
  useEffect(() => {
    let sentinela: { release?: () => Promise<void> } | null = null;
    let cancelado = false;
    (async () => {
      try {
        const wl = (navigator as unknown as { wakeLock?: { request?: (t: string) => Promise<unknown> } })
          .wakeLock;
        if (!wl?.request) return;
        sentinela = (await wl.request('screen')) as { release?: () => Promise<void> };
        if (cancelado) {
          void sentinela?.release?.();
          sentinela = null;
        }
      } catch {
        /* sem suporte ou permissão */
      }
    })();
    return () => {
      cancelado = true;
      try {
        void sentinela?.release?.();
      } catch {
        /* já soltou */
      }
    };
  }, []);

  function avancar() {
    setIndice((i) => Math.min(passos.length - 1, i + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function voltar() {
    setIndice((i) => Math.max(0, i - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function ciclarFonte() {
    const proximo = (escalaIdx + 1) % ESCALAS.length;
    setEscalaIdx(proximo);
    try {
      localStorage.setItem(CHAVE_FONTE, String(proximo));
    } catch {
      /* armazenamento indisponível */
    }
  }

  async function aceitar() {
    if (previa || enviandoDesafio || desafioAceito) return;
    setEnviandoDesafio(true);
    try {
      const r = await aceitarDesafio();
      if (r) {
        setDesafioAceito(true);
        setDesafiosHoje(r.desafios_hoje);
        setSituacao((s) => (s ? { ...s, aceitei_desafio: true, desafios_hoje: r.desafios_hoje } : s));
      } else {
        mostrarToast('Não conseguimos registrar agora. Tente de novo.', 4000, 'aviso');
      }
    } finally {
      setEnviandoDesafio(false);
    }
  }

  async function rezar() {
    if (previa || enviandoRezei) return;
    setEnviandoRezei(true);
    try {
      const r = await marcarRezei();
      if (r) {
        setSituacao({
          rezei_hoje: r.rezei_hoje,
          dias_na_semana: r.dias_na_semana,
          pessoas_hoje: r.pessoas_hoje,
          aceitei_desafio: r.aceitei_desafio,
          desafios_hoje: r.desafios_hoje,
          semana: r.semana,
        });
        if (r.aceitei_desafio) setDesafioAceito(true);
        setDesafiosHoje(r.desafios_hoje);
        setClarao(true);
        window.setTimeout(() => avancar(), 450);
        window.setTimeout(() => setClarao(false), 1200);
      } else {
        mostrarToast('Não conseguimos registrar agora. Tente de novo.', 4000, 'aviso');
      }
    } finally {
      setEnviandoRezei(false);
    }
  }

  async function compartilhar() {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Oração de hoje — Se Liga', url: LINK_ORACAO });
      } catch {
        /* a pessoa cancelou o compartilhamento */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(LINK_ORACAO);
      mostrarToast('Link copiado.', 3000, 'sucesso');
    } catch {
      mostrarToast(LINK_ORACAO, 4000);
    }
  }

  async function ativarAvisos() {
    setAtivandoAvisos(true);
    try {
      await ligarAvisosGerais();
      setMostrarAvisos(false);
      mostrarToast('Pronto! Avisos ligados.', 5000, 'sucesso');
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos agora.'), 6000, 'erro');
    } finally {
      setAtivandoAvisos(false);
    }
  }

  function acaoPrimaria() {
    if (!passoAtual) return;
    switch (passoAtual.id) {
      case 'vida':
        if (perguntaAtual < perguntas.length - 1) {
          setPerguntaAtual((i) => i + 1);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          avancar();
        }
        break;
      case 'viva':
        if (desafioAceito) avancar();
        else void aceitar();
        break;
      case 'termine':
        void rezar();
        break;
      case 'final':
        navigate('/');
        break;
      default:
        avancar();
    }
  }

  function acaoVoltar() {
    if (passoAtual?.id === 'vida' && perguntaAtual > 0) {
      setPerguntaAtual((i) => Math.max(0, i - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    voltar();
  }

  // Só deixa o gesto/seta avançar quando o botão só avança (nunca Aceito/Rezei).
  const podeAvancarRapido =
    !!passoAtual &&
    passoAtual.id !== 'termine' &&
    passoAtual.id !== 'final' &&
    !(passoAtual.id === 'viva' && !desafioAceito);

  useEffect(() => {
    function aoTecla(ev: KeyboardEvent) {
      if (ev.key === 'ArrowRight' && podeAvancarRapido) acaoPrimaria();
      else if (ev.key === 'ArrowLeft') acaoVoltar();
    }
    window.addEventListener('keydown', aoTecla);
    return () => window.removeEventListener('keydown', aoTecla);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeAvancarRapido, perguntaAtual, indice, desafioAceito]);

  function aoSoltarToque(clientX: number, clientY: number) {
    if (toqueX.current === null || toqueY.current === null) return;
    const dx = clientX - toqueX.current;
    const dy = clientY - toqueY.current;
    toqueX.current = null;
    toqueY.current = null;
    if (Math.abs(dx) > 70 && Math.abs(dx) > 2 * Math.abs(dy)) {
      if (dx < 0 && podeAvancarRapido) acaoPrimaria();
      else if (dx > 0) acaoVoltar();
    }
  }

  // Rótulo e estado do botão grande.
  let rotuloPrimario = 'Continuar';
  let primarioDesabilitado = false;
  let primarioCarregando = false;
  if (passoAtual) {
    if (passoAtual.id === 'capa') {
      rotuloPrimario = situacao?.rezei_hoje ? 'Rezar de novo' : 'Começar';
    } else if (passoAtual.id === 'biblia') {
      rotuloPrimario = 'Já li';
    } else if (passoAtual.id === 'vida') {
      rotuloPrimario = perguntaAtual < perguntas.length - 1 ? 'Próxima pergunta' : 'Continuar';
    } else if (passoAtual.id === 'viva') {
      rotuloPrimario = desafioAceito ? 'Continuar' : 'Aceito o desafio';
      primarioDesabilitado = !desafioAceito && previa;
      primarioCarregando = enviandoDesafio && !desafioAceito;
    } else if (passoAtual.id === 'termine') {
      rotuloPrimario = 'Amém · Rezei 🙏';
      primarioDesabilitado = previa;
      primarioCarregando = enviandoRezei;
    } else if (passoAtual.id === 'final') {
      rotuloPrimario = 'Voltar ao início';
    }
  }

  const vars = {
    '--nave': cor.nave,
    '--nave2': cor.nave2,
    '--fita': cor.fita,
    '--brilho': cor.brilho,
    '--escala': String(escala),
  } as CSSProperties;

  return (
    <div
      className="oracao relative flex min-h-[100dvh] flex-col"
      style={vars}
      onTouchStart={(ev) => {
        toqueX.current = ev.touches[0]?.clientX ?? null;
        toqueY.current = ev.touches[0]?.clientY ?? null;
      }}
      onTouchEnd={(ev) => {
        const t = ev.changedTouches[0];
        if (t) aoSoltarToque(t.clientX, t.clientY);
      }}
    >
      <div className="oracao-fundo" aria-hidden>
        {oracao?.imagem_url && <img src={oracao.imagem_url} alt="" />}
      </div>

      {/* Arte da capa (nítida) — some nos demais passos. */}
      {passoAtual?.id === 'capa' && oracao?.imagem_url && (
        <div
          className="capa-arte"
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: '50%',
            right: 'auto',
            width: '100%',
            maxWidth: 560,
            transform: 'translateX(-50%)',
            zIndex: 0,
          }}
        >
          <img src={oracao.imagem_url} alt="" />
        </div>
      )}

      {/* Cabeçalho na identidade do cartão da Início: verde profundo + dourado. */}
      <header
        className="sticky top-0 z-30"
        style={{
          background: '#0E1C15',
          borderBottom: '1px solid rgba(217,178,95,.32)',
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(232,200,119,.10), rgba(0,0,0,0))' }}
        />
        <div className="relative mx-auto flex w-full max-w-[560px] items-center gap-3 px-6 py-3">
          {temOracao && passos.length > 1 ? (
            <BarraProgresso total={passos.length} atual={indice} />
          ) : (
            <div className="flex-1" />
          )}
          {temOracao && (
            <button
              type="button"
              onClick={ciclarFonte}
              aria-label="Tamanho do texto"
              className="aperta flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-[15px] font-semibold text-[#E8C877] transition-colors hover:bg-white/[0.08]"
            >
              Aa
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="Fechar"
            className="aperta flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#E8C877] transition-colors hover:bg-white/[0.08]"
          >
            <i className="ri-close-line text-2xl" aria-hidden />
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-[560px] flex-1 px-6 pb-6 pt-2">
        <div
          className="fita fita-entra"
          aria-hidden
          style={
            {
              right: 24,
              '--fita-h': passoAtual?.id === 'capa' ? '150px' : '64px',
            } as CSSProperties
          }
        />

        {previa && (
          <div
            className="app-entra mb-4 flex items-center gap-2 rounded-[14px] border px-4 py-3 text-[14px] font-semibold"
            style={{
              borderColor: 'rgba(217,178,95,.4)',
              background: 'rgba(217,178,95,.08)',
              color: 'var(--ouro-forte)',
            }}
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={oracao?.aprovada ? 'ri-checkbox-circle-line' : 'ri-time-line'} aria-hidden />
            </span>
            {oracao?.aprovada ? 'Prévia — Aprovada' : 'Prévia — Ainda não aprovada'}
          </div>
        )}

        {carregando && (
          <div className="mt-3 space-y-3">
            <div className="h-6 w-48 animate-pulse rounded bg-white/10" />
            <div className="h-40 animate-pulse rounded-[18px] bg-white/10" />
            <div className="h-40 animate-pulse rounded-[18px] bg-white/10" />
          </div>
        )}

        {semOracao && (
          <div
            className="app-entra mt-3 rounded-[18px] border p-6 text-center"
            style={{ borderColor: 'rgba(217,178,95,.3)', background: 'rgba(255,255,255,.03)' }}
          >
            <span
              className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
              style={{ background: 'rgba(217,178,95,.15)', color: 'var(--ouro-forte)' }}
            >
              <i className="ri-heart-3-line text-2xl" aria-hidden />
            </span>
            <p className="mt-4 text-[17px] font-semibold text-[color:var(--texto)]">
              A oração de hoje ainda está sendo preparada.
            </p>
            <button
              type="button"
              onClick={() => navigate('/liturgia')}
              className="botao-contorno aperta mt-4 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 text-[15px]"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-book-open-line" aria-hidden />
              </span>
              Ler a liturgia de hoje
            </button>
          </div>
        )}

        {!carregando && temOracao && oracao && passoAtual && (
          <ConteudoPasso
            passo={passoAtual}
            oracao={oracao}
            situacao={situacao}
            cor={cor}
            previa={previa}
            desafioAceito={desafioAceito}
            desafiosHoje={desafiosHoje}
            ativandoAvisos={ativandoAvisos}
            mostrarAtivarAvisos={mostrarAvisos}
            perguntaAtual={perguntaAtual}
            perguntas={perguntas}
            aoCompartilhar={() => void compartilhar()}
            aoAtivarAvisos={() => void ativarAvisos()}
            aoInstalar={() => navigate('/instalar')}
          />
        )}
      </main>

      {!carregando && temOracao && passoAtual && (
        <footer className="sticky bottom-0 z-30">
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ background: `linear-gradient(0deg, ${cor.nave} 62%, rgba(0,0,0,0))` }}
          />
          <div className="relative mx-auto w-full max-w-[560px] px-6 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4">
            <button
              type="button"
              onClick={acaoPrimaria}
              disabled={primarioDesabilitado || primarioCarregando}
              className="botao-ouro aperta flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-6 text-[16px] transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {primarioCarregando ? (
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" aria-hidden />
                </span>
              ) : null}
              {rotuloPrimario}
            </button>
            {indice > 0 && passoAtual.id !== 'final' && (
              <button
                type="button"
                onClick={acaoVoltar}
                className="aperta mx-auto mt-1 block cursor-pointer whitespace-nowrap px-4 py-2 text-[15px] font-semibold text-[color:var(--texto-2)]"
              >
                Voltar
              </button>
            )}
          </div>
        </footer>
      )}

      {clarao && <div className="oracao-clarao" aria-hidden />}
    </div>
  );
}